import RestoreIcon from '@mui/icons-material/Restore';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Box, Button, Stack, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { PRIMARY_ALPHA } from '../ColumnConfiguratorDrawer.styles';

interface DrawerFooterProps {
  onCancel: () => void;
  onResetToDefaults: () => void;
  onSaveView: () => void;
  onApply: () => void;
}

export function DrawerFooter({ onCancel, onResetToDefaults, onSaveView, onApply }: DrawerFooterProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { main: primary, dark: primaryDark } = theme.palette.primary;

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        bgcolor: 'var(--white-97)',
        backdropFilter: 'blur(8px)',
        borderTop: '1px solid var(--gray-200)',
        flexShrink: 0,
        boxShadow: '0 -4px 16px var(--shadow-8)',
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={onCancel}
            sx={{
              flex: 1,
              borderRadius: '8px',
              borderWidth: 1.5,
              borderColor: 'var(--gray-300)',
              color: 'var(--gray-500)',
              fontWeight: theme.typography.fontWeightMedium,
              textTransform: 'none',
              fontSize: 'var(--font-button)',
              py: 0.75,
              '&:hover': { borderColor: 'var(--gray-400)', bgcolor: 'var(--gray-50)', borderWidth: 1.5 },
            }}
          >
            {t('columnConfigurator.cancel')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onResetToDefaults}
            startIcon={<RestoreIcon sx={{ fontSize: 'var(--icon-action)' }} />}
            sx={{
              flex: 1,
              borderRadius: '8px',
              borderWidth: 1.5,
              borderColor: alpha(primary, PRIMARY_ALPHA.buttonBorder),
              color: primary,
              fontWeight: theme.typography.fontWeightMedium,
              textTransform: 'none',
              fontSize: 'var(--font-button)',
              py: 0.75,
              '&:hover': {
                borderColor: primary,
                bgcolor: alpha(primary, PRIMARY_ALPHA.buttonBgHover),
                borderWidth: 1.5,
              },
            }}
          >
            {t('columnConfigurator.resetToDefaults')}
          </Button>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={onSaveView}
            startIcon={<SaveAltIcon sx={{ fontSize: 'var(--icon-action)' }} />}
            sx={{
              flex: 1,
              borderRadius: '8px',
              borderWidth: 1.5,
              borderColor: alpha(primary, PRIMARY_ALPHA.buttonBorder),
              color: primary,
              fontWeight: theme.typography.fontWeightMedium,
              textTransform: 'none',
              fontSize: 'var(--font-button)',
              py: 0.75,
              '&:hover': {
                borderColor: primary,
                bgcolor: alpha(primary, PRIMARY_ALPHA.buttonBgHover),
                borderWidth: 1.5,
              },
            }}
          >
            {t('columnConfigurator.saveView')}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={onApply}
            sx={{
              flex: 1,
              borderRadius: '8px',
              bgcolor: primary,
              color: 'white',
              fontWeight: theme.typography.fontWeightSemiBold,
              textTransform: 'none',
              fontSize: 'var(--font-button)',
              py: 0.75,
              boxShadow: `0 2px 8px ${alpha(primary, PRIMARY_ALPHA.applyShadow)}`,
              '&:hover': {
                bgcolor: primaryDark,
                boxShadow: `0 4px 12px ${alpha(primary, PRIMARY_ALPHA.applyShadowHover)}`,
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'translateY(0)' },
            }}
          >
            {t('columnConfigurator.apply')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
